import React, { useContext } from 'react'
import { FacetGroupName, FacetGroupStatus, FacetName, FilterName } from '../../__generated__/fed-graphql-types'
import { InfoBanner } from '../info-banner/info-banner'
import { FieldsetPanel } from '../fieldset-panel/fieldset-panel'
import { ExpandableFilterHeader } from '../expandable-filter-header/expandable-filter-header'
import { UserLocationContext } from '../../contexts/user-location-context'
import {
    StyledClearFacetGroupButton,
    StyledClearAllContainer,
    StyledExpandableContent,
    StyledExpandableFilter,
} from './expandable-filter.styles'
import { ExpandableFilterProps } from './types'
import { useDelayUnmount } from '../../hooks/use-delay-unmount'
import { useFormState } from '../../forms/hocs/form.state'
import { useForm } from '../../forms/hocs/form.context'
import { useATTrackInteraction } from '@at/consumer-web-snowplow'

export const filterModalPopupDelay = 300

export const ExpandableFilter = ({
    icon,
    title,
    group,
    children,
    singularColumn = true,
    badge,
    alwaysOpen = false,
}: ExpandableFilterProps) => {
    const filterTitle = group?.title || title || ''

    const { error: hasPostcodeError } = useContext(UserLocationContext)
    const helpText = group?.helpText
    const clearButtonLabel = group?.clearButtonLabel
    const handleClearSectionInteraction = useATTrackInteraction({
        trackingLabel: `clear-filter-${group?.expandableGroupName?.replace('_', '-')}`,
    })
    const isSelected = !!group?.selectedValuesSummary
    const { toggleFilterGroup, isFilterGroupOpen } = useFormState()

    const shouldFilterGroupBeOpen = () => {
        if (group?.expandableGroupName === FacetGroupName.distance && hasPostcodeError) {
            return true
        }

        if (group?.expandableGroupName) {
            return alwaysOpen || isFilterGroupOpen(group?.expandableGroupName)
        }

        return alwaysOpen
    }
    const isOpen = shouldFilterGroupBeOpen()
    const shouldRenderChild = useDelayUnmount(isOpen, filterModalPopupDelay)

    const handleExpandableFilters = () => {
        if (!hasPostcodeError && !alwaysOpen) {
            if (group?.expandableGroupName) {
                toggleFilterGroup(group?.expandableGroupName)
            }
        }
    }
    const hasClearButton =
        group?.expandableGroupName === FacetGroupName.body_type ||
        group?.expandableGroupName === FacetGroupName.colour ||
        group?.expandableGroupName === FacetGroupName.fuel_type ||
        group?.expandableGroupName === FacetGroupName.seats ||
        group?.expandableGroupName === FacetGroupName.doors
    const { setLocalFilterPills, updateSelected } = useForm()

    const filterIconWithDefaultProps = icon
        ? React.cloneElement(icon as React.ReactElement, {
              customSize: { height: 18, width: 18 },
          })
        : null

    const disabled = group?.status !== FacetGroupStatus.ENABLED

    if (!group) return null

    const clearSelectedValuesEditableChips = () => {
        switch (group?.expandableGroupName) {
            case FacetGroupName.body_type:
                updateSelected(FacetName.body_type, [{ filter: FilterName.body_type, selected: [undefined] }], true)
                break
            case FacetGroupName.colour:
                updateSelected(FacetName.colour, [{ filter: FilterName.colour, selected: [undefined] }], true)
                break
            case FacetGroupName.fuel_type:
                updateSelected(FacetName.fuel_type, [{ filter: FilterName.fuel_type, selected: [undefined] }], true)
                break
            case FacetGroupName.seats:
                updateSelected(FacetName.seats, [{ filter: FilterName.seats_values, selected: [undefined] }], true)
                break
            case FacetGroupName.doors:
                updateSelected(
                    FacetName.doors_values,
                    [{ filter: FilterName.doors_values, selected: [undefined] }],
                    true
                )
                break
        }

        setLocalFilterPills((prev) =>
            prev
                .map((filterPill) => ({
                    ...filterPill,
                    pills: filterPill.pills.filter((pill) => pill.facetGroupName !== group?.expandableGroupName),
                }))
                .filter((filterPill) => filterPill.pills.length > 0)
        )
    }

    return (
        <StyledExpandableFilter id={group.expandableGroupName} disabled={disabled}>
            <ExpandableFilterHeader
                className={group.expandableGroupName}
                icon={filterIconWithDefaultProps}
                title={filterTitle}
                selectedValuesSummary={group?.selectedValuesSummary}
                isOpen={shouldRenderChild}
                onClick={handleExpandableFilters}
                disabled={disabled}
                badge={badge}
                alwaysOpen={alwaysOpen}
            />

            {Boolean(shouldRenderChild) && (
                <StyledExpandableContent isFilterOpen={isOpen}>
                    <FieldsetPanel singularColumn={singularColumn}>
                        {clearButtonLabel && hasClearButton ? (
                            <StyledClearAllContainer
                                data-testid={`${clearButtonLabel.replace(' ', '-').toLowerCase()}-container`}
                            >
                                <StyledClearFacetGroupButton
                                    text={clearButtonLabel}
                                    displayType="tertiary"
                                    disabled={!isSelected}
                                    data-testid={clearButtonLabel.replace(' ', '-').toLowerCase()}
                                    onInteraction={() => {
                                        clearSelectedValuesEditableChips()
                                        handleClearSectionInteraction()
                                    }}
                                />
                            </StyledClearAllContainer>
                        ) : null}
                        {children}
                        {singularColumn ? <InfoBanner paragraphs={helpText} disabled={disabled} /> : null}
                    </FieldsetPanel>
                    {!singularColumn ? <InfoBanner paragraphs={helpText} disabled={disabled} /> : null}
                </StyledExpandableContent>
            )}
        </StyledExpandableFilter>
    )
}
